import React, { FC, ReactNode, useCallback, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { twMerge } from 'tailwind-merge';

import { useFileManagementContext } from '@/providers/FileManagementProvider';

type CardProps = React.HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  className?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  forceHovered?: boolean;
  onCardInView?: () => void;
  onClick?: (e: React.MouseEventHandler<HTMLDivElement>) => void | undefined;
};

export const Card: FC<CardProps> = ({
  children,
  className = '',
  onClick = undefined,
  onMouseEnter = undefined,
  onMouseLeave = undefined,
  forceHovered = false,
  onCardInView = undefined,
  ...props
}) => {
  const { activeFile } = useFileManagementContext();

  const handleInViewChange = useCallback(
    (inView: boolean) => {
      if (inView && onCardInView) onCardInView();
    },
    [onCardInView]
  );

  const { ref: inViewRef } = useInView({
    onChange: handleInViewChange,
    triggerOnce: true,
  });
  const cardRef = useRef<HTMLDivElement>(null);

  const conditionallyScrollTheCardIntoView = useCallback(() => {
    if (!cardRef.current) return;

    const userIsViewingAFile = !!activeFile;

    if (!userIsViewingAFile) {
      cardRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [activeFile, cardRef]);

  useEffect(() => {
    conditionallyScrollTheCardIntoView();
  }, []);

  const handleOnClick = useCallback((e) => onClick?.(e), [onClick]);

  return (
    // eslint-disable-next-line jsx-a11y/interactive-supports-focus, jsx-a11y/no-static-element-interactions
    <div
      {...props}
      ref={cardRef}
      className={twMerge(
        'block w-full justify-start bg-gray-10 px-2 py-1 align-top outline outline-[1px] outline-gray-20 transition-all',
        'hover:cursor-pointer hover:shadow-elevation-3 hover:outline-green-50',
        forceHovered && 'shadow-elevation-3 outline-green-50 ',
        className
      )}
      onClick={handleOnClick}
      onFocus={() => {}}
      onKeyDown={handleOnClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div ref={inViewRef} className="size-full">
        {children}
      </div>
    </div>
  );
};
